import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from './components/ErrorBoundary';
import { LoginPage } from './components/LoginPage';
import { Layout } from './components/Layout';
import { ChatInterface } from './components/ChatInterface';
import { SettingsPage } from './components/SettingsPage';
import { KnowledgePage } from './components/KnowledgePage';
import { UserManagementPage } from './components/UserManagementPage';
import { OpenQuestionsPage } from './components/OpenQuestionsPage';
import { BackupPage } from './components/BackupPage';
import { ForcePasswordChange } from './components/ForcePasswordChange';
import { HauslistePage } from './components/HauslistePage';
import { AnreiseAutozugPage } from './components/AnreiseAutozugPage';
import { AnreiseFlugPage } from './components/AnreiseFlugPage';
import { AnreiseFaehrePage } from './components/AnreiseFaehrePage';
import { ImportPage } from './components/ImportPage';
import { MailConfigPage } from './components/MailConfigPage';
import { MailInboxPage } from './components/MailInboxPage';
import { MailOwnersPage } from './components/MailOwnersPage';
import { MailQuickActionsAdmin } from './components/MailQuickActionsAdmin';
import { AiReplyConfigPage } from './components/AiReplyConfigPage';
import RuleSuggestionsPage from './components/RuleSuggestionsPage';
import { UserProfilePage } from './components/UserProfilePage';
import { SystemStatusPage } from './components/SystemStatusPage';
import OeffnungszeitenPage from './components/OeffnungszeitenPage';
import { OeffnungszeitenAdmin } from './components/OeffnungszeitenAdmin';
import '@/App.css';

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // useRef: stellt sicher, dass der Axios-Interceptor immer den aktuellen User-Status kennt (kein stale closure)
  const userRef = useRef(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        // Prüfe ob Token vorhanden (neues System) oder altes Format
        if (parsed.token || parsed.role) {
          setUser(parsed);
        } else {
          localStorage.removeItem('gb-sylt-user');
        }
      } catch (e) {
        console.error('Failed to parse saved user', e);
        localStorage.removeItem('gb-sylt-user');
      }
    }
    setIsLoading(false);
  }, []);

  // userRef synchron mit user-State halten
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('gb-sylt-user', JSON.stringify(userData));
  };

  const handleLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('gb-sylt-user');
  }, []);

  // C) Globaler 401-Guard: Bei jedem 401 Response → Forced Logout
  // Einmalig registrieren (kein user in der Dep-Liste) - nutzt userRef statt Closure
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && userRef.current) {
          console.warn('[Auth Guard] 401 erhalten – Session abgelaufen, Forced Logout');
          handleLogout();
          window.location.replace('/login');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePasswordChanged = () => {
    // Update user to remove must_change_password flag
    if (user) {
      const updatedUser = { ...user, must_change_password: false };
      setUser(updatedUser);
      localStorage.setItem('gb-sylt-user', JSON.stringify(updatedUser));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-primary items-center justify-center mb-4 animate-pulse">
            <span className="text-primary-foreground font-bold text-2xl">GB</span>
          </div>
          <p className="text-muted-foreground">Lädt...</p>
        </div>
      </div>
    );
  }

  // Check if user must change password
  const mustChangePassword = user?.must_change_password === true;

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <Routes>
          {/* Anreise-Seiten sind IMMER zugänglich (auch ohne Login) */}
          <Route path="/anreise/autozug" element={<AnreiseAutozugPage />} />
          <Route path="/anreise/flug" element={<AnreiseFlugPage />} />
          <Route path="/anreise/faehre" element={<AnreiseFaehrePage />} />
          
          {/* Oeffnungszeiten - oeffentlich ohne Login */}
          <Route path="/oeffnungszeiten" element={<OeffnungszeitenPage />} />
          
          {!user ? (
            <>
              <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : mustChangePassword ? (
            <>
              <Route path="*" element={
                <ForcePasswordChange 
                  user={user} 
                  onPasswordChanged={handlePasswordChanged} 
                  onLogout={handleLogout} 
                />
              } />
            </>
          ) : (
            <Route path="/" element={<Layout user={user} onLogout={handleLogout} onPasswordChanged={handlePasswordChanged} />}>
              <Route index element={<ChatInterface user={user} />} />
              {/* Hausliste available for all logged-in users (employees and admins) */}
              {user && (user.role === 'admin' || user.role === 'employee') && (
                <>
                  <Route path="/hausliste" element={<HauslistePage user={user} />} />
                  <Route path="/profile" element={<UserProfilePage user={user} />} />
                  <Route path="/admin/oeffnungszeiten" element={<OeffnungszeitenAdmin />} />
                </>
              )}
              {user && user.role === 'admin' && (
                <>
                  <Route path="/knowledge" element={<KnowledgePage user={user} />} />
                  <Route path="/open-questions" element={<OpenQuestionsPage user={user} />} />
                  <Route path="/settings" element={<SettingsPage user={user} />} />
                  <Route path="/users" element={<UserManagementPage user={user} />} />
                  <Route path="/backup" element={<BackupPage user={user} />} />
                  <Route path="/import" element={<ImportPage user={user} />} />
                  <Route path="/system-status" element={<SystemStatusPage user={user} />} />
                </>
              )}
              {/* Mail Automation - nur für UMitza (Admin mit speziellem Username) */}
              {user && (user.username === 'UMitza' || user.username === 'admin') && (
                <>
                  <Route path="/mail/config" element={<MailConfigPage user={user} />} />
                  <Route path="/mail/inbox" element={<MailInboxPage user={user} />} />
                  <Route path="/mail/owners" element={<MailOwnersPage user={user} />} />
                  <Route path="/mail/quick-actions" element={<MailQuickActionsAdmin />} />
                  <Route path="/mail/ai-config" element={<AiReplyConfigPage />} />
                  <Route path="/mail/rules" element={<RuleSuggestionsPage user={user} />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}
          </Routes>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;